import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, HelpCircle } from 'lucide-react';
import { api } from '@/lib/api-client';
import { SEOHead } from '@/components/seo';
import { PAGE_SEO } from '@/lib/seo-config';

const faqs = [
  { q: 'What is OpenClaw?', a: 'OpenClaw is an open-source personal AI assistant that runs on your machine. It can control your computer, browse the web, send messages, and automate tasks via chat apps like Telegram and Discord.' },
  { q: 'Do I need my own AI API keys?', a: 'Yes, you need to bring your own API keys for your preferred LLM provider (Anthropic, OpenAI, Google, etc.). ClawStinger manages the infrastructure; you control the AI.' },
  { q: 'Can I upgrade or downgrade anytime?', a: 'Yes. You can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the next billing cycle.' },
  { q: 'What happens after the free trial?', a: 'After your trial ends, you can choose a plan or your instance will be paused. No charges until you subscribe.' },
  { q: 'Do you offer refunds?', a: 'We offer a 7-day money-back guarantee on all plans. If you\'re not satisfied, contact support for a full refund.' },
  { q: 'What regions are available?', a: 'We support 8 global regions: US East, US West, EU West, EU Central, Asia South, Asia Northeast, South America, and Africa.' },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [pricing, setPricing] = useState({ plans: [], trial: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getPricing()
      .then(data => setPricing(data))
      .catch(err => console.error('Failed to load pricing:', err))
      .finally(() => setLoading(false));
  }, []);

  const { plans, trial } = pricing;

  // Convert FAQs for structured data
  const faqData = faqs.map(f => ({ question: f.q, answer: f.a }));

  return (
    <div className="bg-[#050505] min-h-screen pt-24">
      <SEOHead
        title={PAGE_SEO.pricing.title}
        description={PAGE_SEO.pricing.description}
        keywords={PAGE_SEO.pricing.keywords}
        url="/pricing"
        faq={faqData}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Pricing', url: '/pricing' },
        ]}
      />
      <section className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h1 data-testid="pricing-page-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 section-marker">Pricing</h1>
            <p className="text-base md:text-lg text-zinc-400 mb-8">
              Start free, scale when ready. 
              {trial?.is_enabled && ` All plans include a ${trial.trial_days}-day trial.`}
            </p>
            <div data-testid="billing-toggle" className="inline-flex items-center gap-3 bg-[#111] border border-zinc-800 rounded-sm p-1">
              <button onClick={() => setAnnual(false)} className={`px-4 py-2 text-sm font-mono rounded-sm transition-all ${!annual ? 'bg-emerald-500/10 text-emerald-400' : 'text-zinc-500 hover:text-white'}`}>Monthly</button>
              <button onClick={() => setAnnual(true)} className={`px-4 py-2 text-sm font-mono rounded-sm transition-all ${annual ? 'bg-emerald-500/10 text-emerald-400' : 'text-zinc-500 hover:text-white'}`}>
                Annual <span className="text-emerald-400 text-xs ml-1">Save 17%</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
              {[1,2,3].map(i => (
                <div key={i} className="rounded-sm p-8 border border-zinc-800 bg-[#111] animate-pulse">
                  <div className="h-6 bg-zinc-800 rounded w-1/3 mb-4"></div>
                  <div className="h-12 bg-zinc-800 rounded w-1/2 mb-6"></div>
                  <div className="space-y-2">
                    {[1,2,3,4,5].map(j => <div key={j} className="h-4 bg-zinc-800 rounded"></div>)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24 items-stretch">
              {plans.map((plan, i) => (
                <div
                  key={plan.id || i}
                  data-testid={`pricing-tier-${plan.name}`}
                  className={`rounded-sm p-8 border transition-all flex flex-col ${plan.is_highlighted ? 'bg-emerald-500/5 border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.1)] relative' : 'bg-[#111] border-zinc-800'}`}
                >
                  {plan.is_highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-black text-xs font-mono font-bold rounded-sm">
                      MOST POPULAR
                    </div>
                  )}
                  <h3 className="text-white font-mono font-bold text-xl mb-1">{plan.display_name}</h3>
                  <p className="text-zinc-500 text-sm mb-6">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-5xl font-mono font-bold text-white">${annual ? plan.price_annual : plan.price_monthly}</span>
                    <span className="text-zinc-500 font-mono text-sm">/mo</span>
                  </div>
                  <ul className="space-y-3 flex-grow">
                    {plan.features?.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-zinc-400">
                        <Check size={14} className="text-emerald-400 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    data-testid={`pricing-cta-${plan.name}`}
                    className={`block w-full text-center font-mono font-bold text-sm rounded-sm py-3 mt-8 transition-all click-scale ${plan.is_highlighted ? 'bg-emerald-500 text-black hover:bg-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'border border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                  >
                    {trial?.is_enabled ? trial.trial_cta_text : 'Get Started'}
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* FAQ */}
          <div>
            <h2 data-testid="faq-title" className="text-2xl md:text-3xl font-bold text-white mb-8 section-marker">Frequently Asked</h2>
            <div className="space-y-2">
              {faqs.map((faq, i) => (
                <div key={i} data-testid={`faq-${i}`} className="border border-zinc-800 rounded-sm overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/[0.02] transition-all"
                  >
                    <span className="text-sm font-mono text-zinc-300">{faq.q}</span>
                    <HelpCircle size={16} className={`text-zinc-600 transition-transform ${openFaq === i ? 'rotate-180 text-emerald-400' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-4 text-sm text-zinc-500 leading-relaxed animate-fade-in">{faq.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
